import { ReactNode, ReactElement } from 'react';
import { Provider } from 'react-redux';
import { persistor, store } from '../../redux/store';
import { PersistGate } from 'redux-persist/integration/react';
import { SnackbarProvider } from 'notistack';
import { MsalProvider, MsalAuthenticationTemplate } from '@azure/msal-react';
import { EventMessage, EventType, AuthenticationResult, InteractionType } from '@azure/msal-browser';
import { getInstance } from '../../config/authConfig';
import ThemeProvider from './ThemeProvider';
import CloseSnackbarButton from '../molecules/CloseSnackbarButton/CloseSnackbarButton';

export interface AppProvidersProps {
  children: ReactNode;
}

export const AppProviders = ({ children }: AppProvidersProps): ReactElement => {
  const pca = getInstance();

  // If user is already logged in then set the account as active.
  const accounts = pca.getAllAccounts();
  if (accounts.length > 0) {
    pca.setActiveAccount(accounts[0]);
  }

  // Make sure on successful login that we set the account as active.
  pca.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as AuthenticationResult;
      const account = payload.account;
      pca.setActiveAccount(account);
    }
  });

  return (
    <MsalProvider instance={pca}>
      <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <ThemeProvider>
              <SnackbarProvider
                maxSnack={3}
                preventDuplicate
                action={(snackbarKey) => <CloseSnackbarButton snackbarKey={snackbarKey} />}
                autoHideDuration={null}
              >
                {children}
              </SnackbarProvider>
            </ThemeProvider>
          </PersistGate>
        </Provider>
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
};
